import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
import SuccessNotification from '../components/SuccessNotification';
import { publicAPI } from '../services/apiService';
import PageTitle from '../components/PageTitle';
import PriceCalculator from './PriceCalculator';

const Booking = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    service: '',
    tariff: '',
    message: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [services, setServices] = useState([]);
  const [availableTariffs, setAvailableTariffs] = useState([]);
  const [isConsentGiven, setIsConsentGiven] = useState(false);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    loadServicesAndTariffs();
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const response = await publicAPI.getDocuments();
      const loadedDocuments = response.data.documents || [];
      setDocuments(loadedDocuments.filter(doc => doc.active));
    } catch (error) {
      console.error('Error loading documents:', error);
      setDocuments([]);
    }
  };

  const loadServicesAndTariffs = async () => {
    try {
      // Load services (which now contain their own tariffs)
      const servicesResponse = await publicAPI.getServices();
      const loadedServices = servicesResponse.data.services || [];
      setServices(loadedServices);

      console.log('Loaded services with tariffs:', loadedServices.map(s => ({
        id: s.id, 
        name: s.name_de, 
        tariffs: s.tariffs ? s.tariffs.map(t => `${t.name_de} - ${t.price}`) : 'No tariffs'
      })));
    } catch (error) {
      console.error('Error loading services and tariffs:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      // Clear tariff when service changes
      ...(name === 'service' && { tariff: '' })
    });

    // Update available tariffs when service changes
    if (name === 'service') {
      const selectedService = services.find(service => service.id === value);
      console.log('Selected service:', selectedService);
      
      if (selectedService && selectedService.tariffs && selectedService.tariffs.length > 0) {
        console.log('Using service-specific tariffs:', selectedService.tariffs);
        setAvailableTariffs(selectedService.tariffs);
      } else {
        console.log('No tariffs available for this service');
        // No tariffs available for this service
        setAvailableTariffs([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Submit booking form to backend API
      const response = await fetch(`${window.location.origin}/api/public/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit booking');
      }

      // Reset form
      setFormData({ name: '', email: '', phone: '', date: '', service: '', tariff: '', message: '' });
      setShowSuccess(true);
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert(t('booking.form.errorMessage', 'Fehler beim Senden der Buchungsanfrage. Bitte versuchen Sie es später erneut.'));
    }
  };

  return (
    <div className="bg-black text-white">
      <PageTitle 
        title={t('pageTitles.booking')}
        description={currentLang === 'de' 
          ? "Buchen Sie Ihre Fotosession - Professionelle Fotografie in Berlin"
          : "Забронируйте фотосессию - Профессиональная фотография в Берлине"
        }
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-20">
        <div className="absolute inset-0 bg-black opacity-40 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1498335746477-0c73d7353a07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0fGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc1NzUwNzM2OHww&ixlib=rb-4.1.0&q=85')`
          }}
        ></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold mb-6"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {t('booking.title')}
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            {t('booking.subtitle')}
          </p>
        </div>
      </section>

      {/* Mini Calculator Section */}
      <section className="py-12 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <PriceCalculator compact={true} />
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-900 p-8 lg:p-12 border border-gray-800">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div>
                <h2 
                  className="text-2xl font-bold mb-6"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {t('booking.form.personalData')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="flex items-center text-sm font-medium text-gray-300 mb-2">
                      <User className="h-4 w-4 mr-2" />
                      {t('booking.form.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300"
                      placeholder={t('booking.form.namePlaceholder')}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="flex items-center text-sm font-medium text-gray-300 mb-2">
                      <Mail className="h-4 w-4 mr-2" />
                      {t('booking.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300"
                      placeholder={t('booking.form.emailPlaceholder')}
                    />
                  </div>
                </div>
                
                <div className="mt-6">
                  <label htmlFor="phone" className="flex items-center text-sm font-medium text-gray-300 mb-2">
                    <Phone className="h-4 w-4 mr-2" />
                    {t('booking.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-black border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300"
                    placeholder={t('booking.form.phonePlaceholder')}
                  />
                </div>
              </div>

              {/* Booking Details */}
              <div>
                <h2 
                  className="text-2xl font-bold mb-6"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {t('booking.form.bookingDetails')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="date" className="flex items-center text-sm font-medium text-gray-300 mb-2">
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('booking.form.date')}
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      placeholder={currentLang === 'de' ? 'TT.MM.JJJJ' : 'ДД.ММ.ГГГГ'}
                      className="w-full px-4 py-3 bg-black border border-gray-700 text-white focus:outline-none focus:border-white transition-colors duration-300"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="service" className="flex items-center text-sm font-medium text-gray-300 mb-2">
                      <Clock className="h-4 w-4 mr-2" />
                      {t('booking.form.service')}
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 text-white focus:outline-none focus:border-white transition-colors duration-300"
                    >
                      <option value="">{t('booking.form.selectService')}</option>
                      {services.filter(service => service.active !== false).map((service) => (
                        <option key={service.id} value={service.id}>
                          {currentLang === 'ru' ? service.name_ru : service.name_de}
                        </option>
                      ))}
                      {/* Fallback options if no API services */}
                      {services.length === 0 && (
                        <>
                          <option value="wedding">{t('booking.services.wedding')}</option>
                          <option value="portrait">{t('booking.services.portrait')}</option>
                          <option value="architecture">{t('booking.services.architecture')}</option>
                          <option value="events">{t('booking.services.events')}</option>
                          <option value="restoration">{t('booking.services.restoration')}</option>
                          <option value="ai-photoshoot">{t('booking.services.aiPhotoshoot')}</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
                
                {/* Tariff Selection */}
                {formData.service && (
                  <div className="mt-6">
                    <label htmlFor="tariff" className="flex items-center text-sm font-medium text-gray-300 mb-2">
                      <Clock className="h-4 w-4 mr-2" />
                      {t('booking.form.tariff')}
                    </label>
                    <select
                      id="tariff"
                      name="tariff"
                      value={formData.tariff}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 text-white focus:outline-none focus:border-white transition-colors duration-300"
                    >
                      <option value="">{t('booking.form.selectTariff')}</option>
                      {availableTariffs.map((tariff) => (
                        <option key={tariff.id} value={tariff.id}>
                          {currentLang === 'ru' ? tariff.name_ru : tariff.name_de} - {tariff.price}
                        </option>
                      ))}
                      {/* Show message if no tariffs available for selected service */}
                      {availableTariffs.length === 0 && formData.service && (
                        <option disabled>{t('booking.form.noTariffsAvailable')}</option>
                      )}
                    </select>
                    
                    {/* Tariff Description */}
                    {formData.tariff && (() => {
                      const selectedTariff = availableTariffs.find(tariff => tariff.id === formData.tariff);
                      if (!selectedTariff) return null;
                      
                      const tariffName = currentLang === 'ru' ? selectedTariff.name_ru : selectedTariff.name_de;
                      const tariffFeatures = currentLang === 'ru' ? selectedTariff.features_ru : selectedTariff.features_de;
                      const tariffDescription = currentLang === 'ru' ? selectedTariff.conditions_ru : selectedTariff.conditions_de;
                      
                      return (
                        <div className="mt-4 p-4 bg-gray-800 border border-gray-700">
                          <h4 className="text-white font-semibold mb-2">
                            {tariffName} - {selectedTariff.price}
                          </h4>
                          {tariffDescription && (
                            <p className="text-gray-300 text-sm mb-3">
                              {tariffDescription}
                            </p>
                          )}
                          <ul className="text-gray-400 text-sm space-y-1">
                            {Array.isArray(tariffFeatures) && tariffFeatures.map((feature, index) => (
                              <li key={index} className="flex items-center">
                                <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="flex items-center text-sm font-medium text-gray-300 mb-2">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {t('booking.form.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 bg-black border border-gray-700 text-white placeholder-gray-400 focus:outline-none focus:border-white transition-colors duration-300 resize-none"
                  placeholder={t('booking.form.messagePlaceholder')}
                />
              </div>

              {/* Data Consent */}
              <div className="bg-gray-900 p-4 border border-gray-700 rounded-lg">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isConsentGiven}
                    onChange={(e) => setIsConsentGiven(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500 mt-0.5"
                  />
                  <div className="text-sm text-gray-300 leading-relaxed">
                    <p className="mb-2">
                      {t('booking.consent.text')}
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <a 
                        href="/datenschutz" 
                        target="_blank"
                        className="text-indigo-400 hover:text-indigo-300 underline"
                      >
                        {t('booking.consent.privacyPolicy')}
                      </a>
                      {documents.length > 0 && (
                        <>
                          <span>•</span>
                          {documents
                            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                            .map((document, index) => {
                              // Determine which file URL to use based on current language
                              let fileUrl = '';
                              let fileName = '';
                              
                              if (currentLang === 'ru') {
                                fileUrl = document.file_url_ru || document.file_url || '';
                                fileName = document.name_ru;
                              } else {
                                fileUrl = document.file_url_de || document.file_url || '';
                                fileName = document.name_de;
                              }
                              
                              // Skip if no file URL available
                              if (!fileUrl) return null;
                              
                              return (
                                <span key={document.id}>
                                  {index > 0 && <span>•</span>}
                                  <a
                                    href={`${window.location.origin}${fileUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-indigo-400 hover:text-indigo-300 underline"
                                  >
                                    {fileName}
                                  </a>
                                </span>
                              );
                            })
                            .filter(Boolean) // Remove null entries
                          }
                        </>
                      )}
                    </div>
                  </div>
                </label>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-6">
                <button
                  type="submit"
                  disabled={!isConsentGiven}
                  className={`px-8 py-4 font-semibold text-lg transition-all duration-300 transform ${
                    isConsentGiven 
                      ? 'bg-white text-black hover:bg-gray-200 hover:scale-105' 
                      : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {t('booking.form.submit')}
                </button>
              </div>
            </form>
          </div>

          {/* Booking Information */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-900 border border-gray-800">
              <Calendar className="h-8 w-8 mx-auto mb-4" />
              <h3 
                className="text-lg font-semibold mb-2"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {t('booking.info.flexibleSchedule')}
              </h3>
              <p className="text-gray-400 text-sm">
                {t('booking.info.flexibleScheduleDesc')}
              </p>
            </div>
            
            <div className="text-center p-6 bg-gray-900 border border-gray-800">
              <Clock className="h-8 w-8 mx-auto mb-4" />
              <h3 
                className="text-lg font-semibold mb-2"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {t('booking.info.quickResponse')}
              </h3>
              <p className="text-gray-400 text-sm">
                {t('booking.info.quickResponseDesc')}
              </p>
            </div>
            
            <div className="text-center p-6 bg-gray-900 border border-gray-800">
              <MessageSquare className="h-8 w-8 mx-auto mb-4" />
              <h3 
                className="text-lg font-semibold mb-2"
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                {t('booking.info.personalConsultation')}
              </h3>
              <p className="text-gray-400 text-sm">
                {t('booking.info.personalConsultationDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Success Notification */}
      <SuccessNotification
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title={t('booking.form.successTitle', 'Buchungsanfrage gesendet!')}
        message={t('booking.form.successMessage')}
      />
    </div>
  );
};

export default Booking;